import type { QueryClient } from '@tanstack/react-query';
import type { ModerationMessage } from '../types';
import { queryKeys } from './queryClient';

export const LIVE_MESSAGE_CACHE_LIMIT = 1_000;

export function compareMessagesChronologically(left: ModerationMessage, right: ModerationMessage): number {
  return left.timestamp - right.timestamp || left.id - right.id;
}

export function normalizeLiveMessages(messages: readonly ModerationMessage[], limit = LIVE_MESSAGE_CACHE_LIMIT): ModerationMessage[] {
  const byId = new Map<number, ModerationMessage>();
  for (const message of messages) byId.set(message.id, message);
  const byArrival = [...byId.values()].sort((left, right) => left.id - right.id);
  const retained = limit > 0 ? byArrival.slice(-limit) : byArrival;
  return retained.sort(compareMessagesChronologically);
}

export function mergeLiveMessage(current: ModerationMessage[] | undefined, message: ModerationMessage): ModerationMessage[] {
  return normalizeLiveMessages([...(current ?? []), message]);
}

export function mergeLiveMessages(current: ModerationMessage[] | undefined, messages: ModerationMessage[]): ModerationMessage[] {
  return normalizeLiveMessages([...(current ?? []), ...messages]);
}

export function reconcileLiveMessageSnapshot(
  current: ModerationMessage[] | undefined,
  snapshot: ModerationMessage[],
): ModerationMessage[] {
  const orderedSnapshot = normalizeLiveMessages(snapshot);
  if (!orderedSnapshot.length || !current?.length) return orderedSnapshot;

  const highestSnapshotId = Math.max(...orderedSnapshot.map(message => message.id));
  const arrivedAfterSnapshot = current.filter(message => message.id > highestSnapshotId);
  return normalizeLiveMessages([...orderedSnapshot, ...arrivedAfterSnapshot]);
}

export function structurallyShareLiveMessages(current: unknown, snapshot: unknown): unknown {
  if (!Array.isArray(snapshot)) return snapshot;
  const currentMessages = Array.isArray(current) ? current as ModerationMessage[] : undefined;
  return reconcileLiveMessageSnapshot(currentMessages, snapshot as ModerationMessage[]);
}

export function latestLiveMessages(messages: readonly ModerationMessage[], limit: number): ModerationMessage[] {
  const ordered = normalizeLiveMessages(messages, 0);
  return limit > 0 ? ordered.slice(-limit) : ordered;
}

export async function clearLiveMessageCache(queryClient: QueryClient): Promise<void> {
  const keys = [queryKeys.messages, queryKeys.moderationMessages, queryKeys.moderationAnswered, queryKeys.moderationPins];
  await Promise.all(keys.map(queryKey => queryClient.cancelQueries({ queryKey, exact: true })));
  queryClient.setQueryData(queryKeys.messages, []);
  queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationMessages, []);
  queryClient.setQueryData<ModerationMessage[]>(queryKeys.moderationAnswered, []);
  queryClient.setQueryData(queryKeys.moderationPins, []);
}
